<template>
  <section class="d-grid gap-3" data-test="reporte-morosidad">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h5 mb-1">Cuentas por cobrar</h2>
        <p class="text-muted small mb-0">
          Lo que deben las cuotas, por antigüedad. Es la deuda del cobro recurrente.
        </p>
      </div>
      <button class="btn btn-outline-dark btn-sm" data-test="btn-csv"
              :disabled="loading || !filas.length" @click="exportar">
        <i class="bi bi-download me-1"></i>Exportar CSV
      </button>
    </header>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1" for="m-corte">Fecha de corte</label>
            <input id="m-corte" v-model="filtros.fecha_corte" type="date"
                   class="form-control form-control-sm" data-test="input-corte">
          </div>
          <div class="col-auto">
            <button class="btn btn-dark btn-sm" data-test="btn-buscar"
                    :disabled="loading" @click="cargar">
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-0" data-test="error">{{ error }}</div>

    <div v-if="totales" class="row g-3">
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Vencido</div>
          <div class="h5 mb-0 text-danger" data-test="total-vencido">
            {{ formatoMonto(totales.total_vencido) }}
          </div>
          <div class="text-muted small">{{ totales.cantidad_vencidas }} cuotas</div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Pendiente total</div>
          <div class="h5 mb-0" data-test="total-pendiente">
            {{ formatoMonto(totales.total_pendiente) }}
          </div>
          <div class="text-muted small">{{ totales.cantidad_pendientes }} cuotas</div>
        </div></div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm"><div class="card-body">
          <div class="text-muted small">Por vencer</div>
          <div class="h5 mb-0" data-test="total-por-vencer">
            {{ formatoMonto(porVencer.monto) }}
          </div>
          <div class="text-muted small">{{ porVencer.cantidad }} cuotas</div>
        </div></div>
      </div>
    </div>

    <!-- El gráfico va ARRIBA de la tabla, nunca en su lugar (RN-89). -->
    <div v-if="totales" class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6 mb-2">Antigüedad de la deuda</h3>
        <GraficoReporte tipo="barras" :series="serieTramos" :categorias="categoriasTramos" />
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-sm mb-0" data-test="tabla">
          <thead>
            <tr>
              <th>Responsable</th><th>Alumno</th><th>Concepto</th>
              <th>Vencimiento</th><th>Antigüedad</th><th class="text-end">Saldo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in filas" :key="f.cr_deuda_id">
              <td>{{ f.responsable_nombre || '—' }}</td>
              <td>{{ f.ficha_nombre || '—' }}</td>
              <td class="small">{{ f.descripcion }}</td>
              <td>{{ f.fecha_vencimiento?.slice(0, 10) }}</td>
              <td><span class="badge" :class="badgeClase(f.tramo)">{{ etiqueta(f.tramo) }}</span></td>
              <td class="text-end">{{ formatoMonto(f.saldo) }}</td>
            </tr>
            <!-- Vacío NO es error: es exactamente la confusión que dejó pasar
                 que el reporte viejo mostrara ₲ 0 durante meses. -->
            <tr v-if="!filas.length && !loading">
              <td colspan="6" class="text-center text-muted py-4" data-test="vacio">
                No hay deuda pendiente para esta fecha de corte.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
// N2 — Morosidad por antigüedad. RG-F2.
// SPEC_REPORTES_DE_GESTION_v0.1.md RN-82, RN-83, RN-85 · D40.
//
// **Esta pantalla ocupa el nombre «Cuentas por cobrar».** Hasta el 2026-09-12
// ese nombre lo tenía el reporte del crédito de almacén, que en producción
// está vacío y va a seguirlo (D25): mostraba ₲ 0 con 22 millones vencidos.

import { ref, computed, onMounted } from 'vue'
import reportsService from '@/services/reportsService'
import GraficoReporte from '@/components/GraficoReporte.vue'

const filas = ref([])
const totales = ref(null)
const loading = ref(false)
const error = ref('')
const filtros = ref({ fecha_corte: '' })

const ETIQUETAS = {
  por_vencer: 'Por vencer', '0-30': '0-30 días',
  '31-60': '31-60 días', '61-90': '61-90 días', '90+': '90+ días'
}
const etiqueta = (t) => ETIQUETAS[t] || t

function badgeClase (tramo) {
  if (tramo === '90+') return 'text-bg-danger'
  if (tramo === '61-90') return 'text-bg-warning'
  if (tramo === 'por_vencer') return 'text-bg-light'
  return 'text-bg-secondary'
}

const porVencer = computed(() =>
  totales.value?.tramos?.find((t) => t.tramo === 'por_vencer') || { monto: 0, cantidad: 0 })

// El gráfico muestra sólo lo VENCIDO: mezclar «por vencer» con la morosidad
// aplasta los tramos que importan, porque casi siempre es la barra más grande.
const tramosVencidos = computed(() =>
  (totales.value?.tramos || []).filter((t) => t.tramo !== 'por_vencer'))

const categoriasTramos = computed(() => tramosVencidos.value.map((t) => etiqueta(t.tramo)))
const serieTramos = computed(() => [{
  name: 'Vencido', data: tramosVencidos.value.map((t) => Number(t.monto))
}])

const formatoMonto = (v) =>
  new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(v) || 0)

async function cargar () {
  loading.value = true
  error.value = ''
  try {
    const data = await reportsService.getMorosidad(filtros.value)
    filas.value = data.filas
    totales.value = data.totales
  } catch (e) {
    error.value = e?.response?.data?.message || 'No se pudo cargar el reporte.'
  } finally {
    loading.value = false
  }
}

async function exportar () {
  try {
    await reportsService.descargarCsv('morosidad', filtros.value, 'morosidad')
  } catch (e) {
    error.value = 'No se pudo exportar el CSV.'
  }
}

onMounted(cargar)
</script>
